import React, { useEffect, useState } from 'react';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import dayjs from 'dayjs';

export type CoachType = 'general' | 'international_master' | 'grand_master';

interface SuccessPageProps {
  orderId?: string;
  amountPaid: number;
  currency?: string;
  programName: string;
  planType: 'personal' | 'group' | 'webinar';
  duration: number;
  sessions: number;
  selectedCoach?: CoachType;
  firstClassDate?: string;
  firstClassTime?: string;
  coachName?: string;
  onViewSchedule?: () => void;
  onDownloadInvoice?: () => void;
}

export function SuccessPage({
  orderId = 'CSOC-2025-11-123456',
  amountPaid,
  currency = 'INR',
  programName,
  planType,
  duration,
  sessions,
  selectedCoach = 'general',
  firstClassDate,
  firstClassTime,
  coachName = 'Sarah Johnson',
  onViewSchedule,
  onDownloadInvoice,
}: SuccessPageProps) {
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    // Trigger animation on mount
    setIsAnimated(true);
  }, []);

  const formatCurrency = (amount: number, curr: string = 'INR'): string => {
    if (curr === 'INR') {
      return `₹${amount.toLocaleString('en-IN')}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
    }).format(amount);
  };

  const getPlanTypeLabel = () => {
    switch (planType) {
      case 'personal':
        return 'Personal 1:1';
      case 'group':
        return 'Small Group';
      case 'webinar':
        return 'Masterclass';
      default:
        return 'Plan';
    }
  };

  // Calculate first class date (3 days from now if not provided)
  const defaultFirstClassDate = firstClassDate || dayjs().add(3, 'days').format('dddd, MMM D, YYYY');
  
  // Format first class time
  let formattedFirstClassTime = firstClassTime || '6:00 PM - 7:30 PM IST';
  if (firstClassTime && firstClassTime.includes('T')) {
    const timeObj = dayjs(firstClassTime);
    formattedFirstClassTime = `${timeObj.format('h:mm A')} - ${timeObj.add(90, 'minutes').format('h:mm A')} IST`;
  } else if (firstClassTime && !firstClassTime.includes('-')) {
    try {
      const timeObj = dayjs(`2000-01-01 ${firstClassTime}`);
      formattedFirstClassTime = `${timeObj.format('h:mm A')} - ${timeObj.add(90, 'minutes').format('h:mm A')} IST`;
    } catch {
      formattedFirstClassTime = firstClassTime;
    }
  }

  // Calculate start date for step 4
  const startDate = firstClassDate 
    ? dayjs(firstClassDate).format('MMM D')
    : dayjs().add(3, 'days').format('MMM D');

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        {/* Success Animation Section */}
        <div style={styles.successSection}>
          <div
            style={{
              ...styles.successBadge,
              transform: isAnimated ? 'scale(1)' : 'scale(0)',
              transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}
          >
            <MaterialCommunityIcons name="check-circle" size={56} color="#4D3F37" />
          </div>
          <h1 style={styles.heading}>Payment Successful!</h1>
          <p style={styles.subtext}>
            Your renewal is confirmed. Welcome to the next level of your learning journey!
          </p>
        </div>

        {/* Confirmation Card */}
        <div style={styles.confirmationCard}>
          {/* Top Row */}
          <div style={styles.topRow}>
            <div>
              <p style={styles.label}>Order ID</p>
              <p style={styles.value}>{orderId}</p>
            </div>
            <div style={styles.amountContainer}>
              <p style={styles.label}>Amount</p>
              <p style={styles.value}>{formatCurrency(amountPaid, currency)}</p>
            </div>
          </div>

          {/* Program Details */}
          <div style={styles.programSection}>
            <p style={styles.label}>Program</p>
            <p style={styles.programValue}>
              {programName} - {getPlanTypeLabel()}
            </p>
          </div>

          {/* Two Column Grid */}
          <div style={styles.gridRow}>
            <div>
              <p style={styles.label}>Duration</p>
              <p style={styles.value}>{duration} months</p>
            </div>
            <div style={styles.sessionsContainer}>
              <p style={styles.label}>Sessions</p>
              <p style={styles.value}>{sessions} classes</p>
            </div>
          </div>
        </div>

        {/* Next Class Info Card */}
        <div style={styles.nextClassCard}>
          <div style={styles.nextClassIcon}>
            <MaterialCommunityIcons name="calendar" size={24} color="#FACF47" />
          </div>
          <div style={styles.nextClassContent}>
            <h2 style={styles.nextClassTitle}>Your First Class</h2>
            <p style={styles.nextClassDetails}>
              {defaultFirstClassDate} • {formattedFirstClassTime}
            </p>
            <p style={styles.nextClassInfo}>
              You'll receive a calendar invite and Zoom link via email within 24 hours. Your coach {coachName} will reach out to introduce herself.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={styles.actionButtons}>
          <button
            style={styles.primaryButton}
            onClick={onViewSchedule}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#D4A017';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#FACF47';
            }}
          >
            <MaterialCommunityIcons name="calendar" size={20} color="#4D3F37" />
            <span>View Schedule</span>
          </button>
          <button
            style={styles.secondaryButton}
            onClick={onDownloadInvoice}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#FAF8F5';
              e.currentTarget.style.borderColor = '#D4A017';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#FFFFFF';
              e.currentTarget.style.borderColor = '#E8E3DC';
            }}
          >
            <MaterialCommunityIcons name="download" size={20} color="#4D3F37" />
            <span>Download Invoice</span>
          </button>
        </div>

        {/* What Happens Next Section */}
        <div style={styles.whatHappensNext}>
          <h2 style={styles.whatHappensTitle}>What Happens Next?</h2>
          
          <div style={styles.stepsContainer}>
            {/* Step 1 */}
            <div style={styles.step}>
              <div style={styles.stepNumber}>1</div>
              <div style={styles.stepContent}>
                <h3 style={styles.stepTitle}>Check Your Email</h3>
                <p style={styles.stepDescription}>
                  You'll receive a confirmation email with your invoice and course details within 5 minutes.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div style={styles.step}>
              <div style={styles.stepNumber}>2</div>
              <div style={styles.stepContent}>
                <h3 style={styles.stepTitle}>Meet Your Coach</h3>
                <p style={styles.stepDescription}>
                  Your assigned coach {coachName} will contact you within 24 hours to discuss your learning goals.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div style={styles.step}>
              <div style={styles.stepNumber}>3</div>
              <div style={styles.stepContent}>
                <h3 style={styles.stepTitle}>Access Course Materials</h3>
                <p style={styles.stepDescription}>
                  Pre-class materials and setup instructions will be shared 3 days before your first session.
                </p>
              </div>
            </div>

            {/* Step 4 */}
            <div style={styles.step}>
              <div style={styles.stepNumber}>4</div>
              <div style={styles.stepContent}>
                <h3 style={styles.stepTitle}>Start Learning!</h3>
                <p style={styles.stepDescription}>
                  Begin your journey on {startDate} and unlock your chess potential.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Support Links */}
        <div style={styles.footer}>
          <p style={styles.footerText}>Questions? We're here to help!</p>
          <div style={styles.footerLinks}>
            <a
              href="#"
              style={styles.footerLink}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#D4A017';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#8A7968';
              }}
            >
              Contact Support
            </a>
            <span style={styles.footerSeparator}>•</span>
            <a
              href="#"
              style={styles.footerLink}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#D4A017';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#8A7968';
              }}
            >
              View FAQs
            </a>
            <span style={styles.footerSeparator}>•</span>
            <a
              href="#"
              style={styles.footerLink}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#D4A017';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#8A7968';
              }}
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Design System Colors
const colors = {
  background: '#FAF8F5',
  primaryCTA: '#FACF47',
  primaryCTAHover: '#D4A017',
  textPrimary: '#4D3F37',
  textSecondary: '#8A7968',
  border: '#E8E3DC',
  white: '#FFFFFF',
  darkGradientStart: '#4D3F37',
  darkGradientEnd: '#6B5C51',
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.background,
    padding: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    fontFamily: 'Inter, Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  content: {
    width: '100%',
    maxWidth: '672px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  successSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginTop: '40px',
    marginBottom: '8px',
  },
  successBadge: {
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    backgroundColor: colors.primaryCTA,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '24px',
  },
  heading: {
    fontSize: '28px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: '0 0 16px 0',
    lineHeight: '1.2',
  },
  subtext: {
    fontSize: '18px',
    color: colors.textSecondary,
    margin: 0,
    lineHeight: '1.5',
    maxWidth: '600px',
  },
  confirmationCard: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `2px solid ${colors.primaryCTA}`,
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: '20px',
    borderBottom: `1px solid ${colors.border}`,
  },
  amountContainer: {
    textAlign: 'right',
  },
  label: {
    fontSize: '13px',
    color: colors.textSecondary,
    margin: '0 0 6px 0',
    fontWeight: 400,
  },
  value: {
    fontSize: '16px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: 0,
  },
  programSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  programValue: {
    fontSize: '16px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: 0,
  },
  gridRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  sessionsContainer: {
    textAlign: 'right',
  },
  nextClassCard: {
    background: `linear-gradient(135deg, ${colors.darkGradientStart} 0%, ${colors.darkGradientEnd} 100%)`,
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    alignItems: 'flex-start',
  },
  nextClassIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: colors.primaryCTA,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  nextClassContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  nextClassTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: colors.white,
    margin: 0,
  },
  nextClassDetails: {
    fontSize: '16px',
    color: colors.white,
    margin: 0,
    lineHeight: '1.5',
  },
  nextClassInfo: {
    fontSize: '14px',
    color: colors.white,
    opacity: 0.9,
    margin: 0,
    lineHeight: '1.5',
  },
  actionButtons: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  primaryButton: {
    backgroundColor: colors.primaryCTA,
    color: colors.textPrimary,
    border: 'none',
    borderRadius: '12px',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'background-color 0.2s ease',
  },
  secondaryButton: {
    backgroundColor: colors.white,
    color: colors.textPrimary,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
  },
  whatHappensNext: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '32px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  whatHappensTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: 0,
    textAlign: 'center',
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  step: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    alignItems: 'flex-start',
  },
  stepNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: colors.primaryCTA,
    color: colors.textPrimary,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '16px',
    fontWeight: 700,
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  stepTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: colors.textPrimary,
    margin: 0,
  },
  stepDescription: {
    fontSize: '14px',
    color: colors.textSecondary,
    margin: 0,
    lineHeight: '1.5',
  },
  footer: {
    textAlign: 'center',
    padding: '32px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  footerText: {
    fontSize: '16px',
    color: colors.textSecondary,
    margin: 0,
  },
  footerLinks: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  footerLink: {
    fontSize: '14px',
    color: colors.textSecondary,
    textDecoration: 'underline',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
  },
  footerSeparator: {
    fontSize: '14px',
    color: colors.textSecondary,
  },
};

export default SuccessPage;





